import { useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

declare global {
  interface Window {
    // Set by the early-capture script in index.html. `beforeinstallprompt`
    // fires before React mounts, so we stash it globally and hydrate here.
    __deferredInstallPrompt?: BeforeInstallPromptEvent | null;
  }
}

export type PwaInstallState = {
  canInstall: boolean;
  isInstalled: boolean;
  isIosSafari: boolean;
  isIosNonSafari: boolean;
  /** Android where we can't one-tap install (event never fired / suppressed). */
  isAndroidManual: boolean;
  /** In-app webview (Telegram/Messenger/Instagram/…) — PWAs can't install here. */
  isInAppBrowser: boolean;
  isReady: boolean;
  install: () => Promise<void>;
};

function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isInAppBrowser(ua: string): boolean {
  // Webviews that embed a browser and cannot install PWAs. Covers the common
  // ways sellers open links: Telegram, WhatsApp, Messenger/Facebook, Instagram,
  // Line, Snapchat, and generic Android WebView (wv).
  return /fban|fbav|fb_iab|instagram|messenger|whatsapp|telegram|line\/|snapchat|; wv\)/.test(ua);
}

function detectPlatform(): {
  isIosSafari: boolean;
  isIosNonSafari: boolean;
  isAndroid: boolean;
  isInApp: boolean;
} {
  if (typeof window === "undefined") {
    return { isIosSafari: false, isIosNonSafari: false, isAndroid: false, isInApp: false };
  }
  const ua = window.navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua);
  const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
  return {
    isIosSafari: isIos && isSafari,
    isIosNonSafari: isIos && !isSafari,
    isAndroid: /android/.test(ua),
    isInApp: isInAppBrowser(ua),
  };
}

/**
 * Best-effort escape from an Android in-app webview into the real browser.
 * Uses an Android `intent://` URL — the package is intentionally NOT pinned so
 * it opens the user's default browser (Chrome or otherwise). This works in
 * permissive webviews (e.g. Telegram); aggressive ones (Facebook/Instagram)
 * sandbox intent navigations, so the on-screen manual instructions remain the
 * fallback when the button does nothing.
 */
export function openInAndroidBrowser(): void {
  if (typeof window === "undefined") return;
  const match = window.location.href.match(/^https?:\/\/(.+)$/i);
  if (!match) return;
  const hostAndPath = match[1];
  window.location.href = `intent://${hostAndPath}#Intent;scheme=https;end`;
}

export function usePwaInstall(): PwaInstallState {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState({
    isInstalled: false,
    isIosSafari: false,
    isIosNonSafari: false,
    isAndroid: false,
    isInApp: false,
    isReady: false,
  });

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const { isIosSafari, isIosNonSafari, isAndroid, isInApp } = detectPlatform();
      setState({
        isInstalled: isStandaloneMode(),
        isIosSafari,
        isIosNonSafari,
        isAndroid,
        isInApp,
        isReady: true,
      });
    });

    // Hydrate from the event captured by index.html before React mounted. This
    // is the common case on first load — the event fires on /login, long before
    // this component exists.
    if (window.__deferredInstallPrompt) {
      setPromptEvent(window.__deferredInstallPrompt);
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
      console.info("[seller-pwa] install prompt available");
    };

    // Fired by the early-capture script when it stashes a fresh event.
    const handleBipCaptured = () => {
      if (window.__deferredInstallPrompt) {
        setPromptEvent(window.__deferredInstallPrompt);
        console.info("[seller-pwa] install prompt available (early-captured)");
      }
    };

    const handleInstalled = () => {
      setPromptEvent(null);
      window.__deferredInstallPrompt = null;
      setState((prev) => ({ ...prev, isInstalled: true }));
      console.info("[seller-pwa] installed");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("bip-captured", handleBipCaptured);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("bip-captured", handleBipCaptured);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const install = async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    console.info("[seller-pwa] install choice", { outcome: choice.outcome });
    // A prompt event can only be used once — clear it either way.
    setPromptEvent(null);
    window.__deferredInstallPrompt = null;
    if (choice.outcome === "accepted") {
      setState((prev) => ({ ...prev, isInstalled: true }));
    }
  };

  return {
    canInstall: !!promptEvent,
    isInstalled: state.isInstalled,
    isIosSafari: state.isIosSafari,
    isIosNonSafari: state.isIosNonSafari,
    // Android, installable in principle, but no one-tap event available —
    // fall back to manual "browser menu → Install app" instructions.
    isAndroidManual: state.isAndroid && !state.isInApp && !promptEvent,
    isInAppBrowser: state.isInApp,
    isReady: state.isReady,
    install,
  };
}
